// @ts-strict-ignore
import isEmpty from 'lodash/isEmpty';
import {AttrsSchema, Component} from 'panel';
import {produce} from 'immer';

import {BannerAction, BannerSize, BannerTheme} from 'common/components/banner/types';
import {USE_THIS_BOARD_IDENTIFIER} from 'common/constants';
import {reporter} from 'common/report/error-reporting';
import {Persistence} from 'common/report/persistence';
import {formatSamplingFactor, getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {unique} from 'common/util/array';
import {getFeatureFlagUrlOverrides, getLocation} from 'common/util/url';
import type {BannerTooltip} from 'common/components/banner/types';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ProjectFeatureFlag} from 'common/report/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {doesKeyExist} from 'common/i18n';

import {openMediaModal} from '../tutorial-video-modal';
import {getBanners, GetBannersResponse} from './api';
import {BannerEventNames, BannerEventProps, REDIRECT_BANNERS, RedirectBannerUrlParams} from './constants';
import {ReportBanner, ReportBannersState} from './types';
import {template} from './template';
import css from './index.inline.styl';

const DEFAULT_HIDDEN_BANNER_IDENTIFIERS = [USE_THIS_BOARD_IDENTIFIER];

export type ReportBannersJsxProps = {
  attrs?: {
    'export-segmentation-limit'?: number;
    'is-segmentation-limit-hit'?: boolean;
    report?: string;
    'report-sampling-factor'?: number;
    'segmentation-limit'?: number;
  };
  props?: {
    // Set this prop to show banners that would otherwise be hidden by default, if they exist.
    revealedBannerIdentifiers?: Set<string>;
  };
};

export class ReportBanners extends Component<
  ReportBannersState,
  ReportBannersJsxProps[`attrs`],
  unknown,
  unknown,
  IronContextRegistry,
  ReportBannersJsxProps
> {
  private declare persistence: Persistence;

  static get attrsSchema(): AttrsSchema<ReportBannersJsxProps[`attrs`]> {
    return {
      'export-segmentation-limit': `number`,
      'is-segmentation-limit-hit': `boolean`,
      report: `string`,
      'report-sampling-factor': `number`,
      'segmentation-limit': `number`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`currentProject` as const, `i18n` as const],
      defaultState: {
        banners: [],
        hiddenBannerIdentifiers: new Set(DEFAULT_HIDDEN_BANNER_IDENTIFIERS),
      },
      helpers: {
        handleBannerAction: (ev, bannerIdx: number) => {
          const {actionName} = ev.detail;

          if (actionName === BannerAction.Dismiss) {
            this.hideBanner(bannerIdx, true);
          } else if (actionName === BannerAction.Tutorial) {
            openMediaModal((mediaModalEv) => {
              this.addErrorToast(mediaModalEv.detail.message);
            });
          } else if (actionName !== BannerAction.None) {
            this.hideBanner(bannerIdx);
          }
        },
        handleBannerTooltipButtonClicked: (ev, bannerName: string) => {
          if (bannerName === `segmentation-limits-banner`) {
            this.dispatchEvent(new CustomEvent(`exportcsvclicked`, ev));
          }
        },
        getReportName: () => {
          return this.attr(`report`) || getReportName();
        },
        areBannersVisible: (): boolean => {
          return this.state.banners.filter((banner) => banner.visible).length > 0;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (getProjectId()) {
      this.syncBanners();
    } else {
      // if projectId and workspaceId are not yet available, that means they
      // are coming from mp.report.globals, so we need to sync after DOMContentLoaded
      // since those globals are initialized after mp-report-banner
      window.addEventListener(
        `DOMContentLoaded`,
        () => {
          this.syncBanners();
        },
        {once: true},
      );
    }
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `report-sampling-factor` && this.isSamplingBannerEnabled()) {
      const reportSamplingFactor = this.attr(`report-sampling-factor`);
      const queryTimeSamplingBanner = this.getQuerySamplingBanner(reportSamplingFactor);
      // Remove the old sampling banner if it exists
      // Add the new sampling banner if the min sampling rate is less than 1.
      const bannersToUpdate: ReportBanner[] = [
        ...this.state.banners.filter((el) => el.name !== queryTimeSamplingBanner.name),
        ...(reportSamplingFactor < 1 ? [queryTimeSamplingBanner] : []),
      ];
      this.update({
        banners: bannersToUpdate,
      });
    } else if ([`is-segmentation-limit-hit`, `segmentation-limit`, `export-segmentation-limit`].includes(attr)) {
      // update segmentation limit banner if any of its attributes change
      const segmentationLimitBanner = this.getSegmentationLimitBanner();
      // Remove the old segmentation limit banner if it exists
      // Add the new segmentation limit banner if it is valid
      const bannersToUpdate: ReportBanner[] = [
        ...this.state.banners.filter((el) => el.name !== `segmentation-limits-banner`),
        ...(segmentationLimitBanner ? [segmentationLimitBanner] : []),
      ];
      this.update({
        banners: bannersToUpdate,
      });
    }
  }

  public set revealedBannerIdentifiers(value: Set<string> | undefined) {
    const hiddenBannerIdentifiers = DEFAULT_HIDDEN_BANNER_IDENTIFIERS.filter((identifier) => {
      return !value?.has(identifier);
    });
    this.update({hiddenBannerIdentifiers: new Set(hiddenBannerIdentifiers)});
  }

  async syncBanners() {
    const projectId = getProjectId();
    this.persistence = new Persistence(`banners:${projectId}`);

    // update static banners first before requesting from API
    this.update({banners: this.getStaticBanners()});
    // don't load any banners if you don't have a project id as it they won't load properly
    if (projectId) {
      try {
        const bannersResponse = await getBanners({
          projectId,
          workspaceId: getWorkspaceId(),
          reportName: this.helpers.getReportName(),
        });

        this.update({
          banners: unique([...this.state.banners, ...this.getBannersFromResponse(bannersResponse)], {
            // ensure duplicate banners (with same title+message) are not added
            // duplicates can occur when switching quickly between reports with no page reload
            hash: (banner) => `${banner.title}|${banner.message}`,
          }),
        });
      } catch (err) {
        reporter.error(err);
      }
    }
  }

  /**
   * Get any banners that don't require making request to banners async endpoint (e.g. based on url params).
   */
  getStaticBanners(): ReportBanner[] {
    const featureFlagsBanner = this.getFeatureFlagsUrlOverrideBanner();
    return [...this.getRedirectBanners(), ...(featureFlagsBanner ? [featureFlagsBanner] : [])];
  }

  /**
   * Get banners to be shown based on query params.
   * Typical use case is showing a confirmation message after a redirect flow.
   */
  getRedirectBanners(): ReportBanner[] {
    const url = new URL(getLocation().toString());
    const banners = Object.values(RedirectBannerUrlParams)
      .map((redirectBannerName) => {
        if (url.searchParams.has(redirectBannerName)) {
          return REDIRECT_BANNERS[redirectBannerName];
        }
        return null;
      })
      .filter(Boolean);

    return banners;
  }

  getSegmentationLimitBanner(): ReportBanner {
    const uxLimit: number | null = this.attr(`segmentation-limit`) ?? null;
    const exportLimit: number | null = this.attr(`export-segmentation-limit`) ?? null;
    const isSegmentationLimitHit: boolean | null = this.attr(`is-segmentation-limit-hit`) ?? null;
    if (!this.isNewSegmentationLimitBannerEnabled() || !isSegmentationLimitHit || !uxLimit) {
      // Only show the banner if the ux segmentation limit has been hit.
      // uxLimit is a required field for the segmentation limit banner.
      return null;
    }

    const {t} = this.getContext(`i18n`);

    // Show a note about exporting if the `exportLimit` is defined and greater than the uxLimit.
    // As of March 2025, all reports have an elevated export limit.
    const tooltip: BannerTooltip | undefined =
      exportLimit && uxLimit < exportLimit
        ? {
            href: `javascript:void(0);`,
            text: t(`components.banners.segmentation-limits.tooltip.text`, {
              exportLimit: exportLimit.toLocaleString(),
            }),
            tooltipText: t(`components.banners.segmentation-limits.tooltip.tooltip-text`),
          }
        : undefined;
    return {
      ctas: null,
      // message: `test segmentation limit banner msg`,
      message: t(`components.banners.segmentation-limits.message`, {
        uxLimit: uxLimit.toLocaleString(),
      }),
      name: `segmentation-limits-banner`,
      onShow: {
        trackEventName: `[mp-banner] Segmentation Limits Banner Shown`,
        trackProps: {
          uxLimit,
          exportLimit,
        },
      },
      size: BannerSize.Small,
      title: ``,
      tooltip,
      type: BannerTheme.Info,
      visible: true,
    };
  }

  getQuerySamplingBanner(samplingFactor: number): ReportBanner {
    const {t} = this.getContext(`i18n`);
    return {
      ctas: null,
      message: t(`components.banners.query-sampling-notice.message`, {
        samplingPercentage: `${formatSamplingFactor(samplingFactor)}`,
      }),
      name: `query-sampling-notice`,
      onShow: {
        trackEventName: `[mp-banner] Sampling Banner Shown`,
        trackProps: {
          samplingFactor,
        },
      },
      size: BannerSize.Small,
      title: ``,
      tooltip: {
        href: `https://docs.mixpanel.com/docs/features/advanced#query-time-sampling`,
        text: t(`components.banners.query-sampling-notice.tooltip.text`),
        tooltipText: t(`components.banners.query-sampling-notice.tooltip.tooltip-text`),
      },
      type: BannerTheme.InfoBlue,
      visible: true,
    };
  }

  getFeatureFlagsUrlOverrideBanner(): ReportBanner {
    const featureFlagUrlOverrides = getFeatureFlagUrlOverrides();
    if (isEmpty(featureFlagUrlOverrides)) {
      return null;
    }

    const flagMessageParts = Object.entries(featureFlagUrlOverrides).map(([flag, isEnabled]) => {
      return isEnabled ? `"${flag}"` : `"${flag}" (disabled)`;
    });
    const message =
      `Url feature flag overrides: ${flagMessageParts.join(`, `)}.` +
      ` It's recommended to set feature flag overrides with HTTP headers.` +
      ` [More info](https://www.notion.so/mpspeed/Project-feature-flag-overrides-4ed8248452744361ac0bab35362a1f58)`;
    return {
      message,
      name: `url-feature-flag-overrides`,
      type: BannerTheme.Info,
      size: BannerSize.Medium,
      ctas: [{actionName: BannerAction.Dismiss, text: `Dismiss`}],
      visible: true,
    };
  }

  bannersResponseToReportBanners(bannersResponse: GetBannersResponse): ReportBanner[] {
    const {t} = this.getContext(`i18n`);
    return bannersResponse.map(
      (bannerData) =>
        ({
          type: bannerData.type,
          ctas: bannerData.ctas?.map((cta) => ({
            ...cta,
            text: doesKeyExist(cta.text) ? t(cta.text) : cta.text,
          })),
          dropdown: bannerData.dropdown,
          identifier: bannerData.identifier,
          message: doesKeyExist(bannerData.message) ? t(bannerData.message) : bannerData.message,
          name: bannerData.name || bannerData.title,
          persistDismissKey: bannerData.persistDismissKey,
          size: bannerData.size,
          title: doesKeyExist(bannerData.title) ? t(bannerData.title) : bannerData.title,
          onShow: bannerData.onShow,
          visible: bannerData.persistDismissKey && this.persistence.get(bannerData.persistDismissKey) ? false : true,
          trackingType: bannerData.trackingType,
        }) as ReportBanner,
    );
  }

  getBannersFromResponse(bannersResponse: GetBannersResponse) {
    const banners = this.bannersResponseToReportBanners(bannersResponse).filter(
      (banner) => banner.visible && banner.type,
    );

    if (banners.length) {
      mixpanel.track(BannerEventNames.BannersShown, {
        [BannerEventProps.NumBannersShown]: banners.length,
        [BannerEventProps.BannerType]: banners.map((banner) => banner.type),
        [BannerEventProps.BannerMessage]: banners.map((banner) => banner.message),
        [BannerEventProps.BannerName]: banners.map((banner) => banner.name),
        [BannerEventProps.BannerTitle]: banners.map((banner) => banner.title),
      });
      banners.forEach((banner) => {
        this.trackBannerEvent(BannerEventNames.BannerShown, banner);
        if (banner.onShow) {
          const {trackEventName, trackProps} = banner.onShow;
          mixpanel.track(trackEventName, trackProps);
        }
      });
    }

    return banners;
  }

  trackBannerEvent(eventName: string, banner: ReportBanner) {
    mixpanel.track(eventName, {
      [BannerEventProps.BannerType]: banner.type,
      [BannerEventProps.BannerMessage]: banner.message,
      [BannerEventProps.BannerName]: banner.name,
      [BannerEventProps.BannerTitle]: banner.title,
      [BannerEventProps.ReportName]: this.helpers.getReportName(),
      [BannerEventProps.ProjectId]: getProjectId(),
      [BannerEventProps.WorkspaceId]: getWorkspaceId(),
    });
  }

  hideBanner(bannerIdx: number, dismissed = false) {
    const {banners} = this.state;
    const banner = banners[bannerIdx];

    if (dismissed) {
      if (banner.persistDismissKey) {
        this.persistence.set(banner.persistDismissKey, Date.now());
      }

      // clean up the URL param, if there was one for this banner
      if (Object.values<string>(RedirectBannerUrlParams).includes(banner.name)) {
        const url = new URL(getLocation().toString());
        url.searchParams.delete(banner.name);
        window.history.replaceState(null, null, url.href);
      }
    }
    this.update({
      banners: produce(banners, (draft) => {
        draft[bannerIdx].visible = false;
      }),
    });
    this.trackBannerEvent(BannerEventNames.BannerHidden, banner);
  }

  addErrorToast(message) {
    addToast({
      type: ToastType.ERROR,
      isError: true,
      message,
    });
  }

  isSamplingBannerEnabled() {
    try {
      const currentProject = this.getContext(`currentProject`)?.get()?.value;
      if (!currentProject) {
        return false;
      }
      return currentProject.hasFeatureFlag(ProjectFeatureFlag.SamplingInfo);
    } catch {
      return false;
    }
  }

  isNewSegmentationLimitBannerEnabled() {
    try {
      const currentProject = this.getContext(`currentProject`)?.get()?.value;
      if (!currentProject) {
        return false;
      }
      return currentProject.hasFeatureFlag(ProjectFeatureFlag.BetterErrors);
    } catch {
      return false;
    }
  }
}

export const ReportBannersEl = defineAndMakeJsxFactory<ReportBannersJsxProps>(`mp-report-banners`, ReportBanners);
export const ReportBannersReact = panel2react<ReportBannersJsxProps>(`mp-report-banners`, ReportBanners);
